import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Check, Trash2, Layers } from "lucide-react";
import { DESIGN_FILE_KINDS, fileToDataUrl, validateDesignFile } from "@/lib/design";

// Renders both legacy single-file uploads (signoff_sheet) and new multi-file
// uploads (design_draft / offline_mom / final_execution_set).
// - design_draft & final_execution_set → flat list of files (append + delete).
// - offline_mom → grouped by upload session (audit trail) with per-file delete.
export default function FileUploadCards({ project, canEdit, onChanged }) {
  const [busy, setBusy] = useState(null);

  const uploadMulti = async (kind, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    // Client-side validation first — bail on the whole batch if anything is invalid.
    for (const f of files) {
      const err = validateDesignFile(f);
      if (err) { toast.error(err); return; }
    }
    setBusy(kind);
    // Chunk to ONE file per HTTP request so aggregate base64 payload never
    // exceeds ingress body caps. For offline_mom, reuse the returned batch_id
    // so all files land in the same audit-trail session.
    const meta = DESIGN_FILE_KINDS.find(k => k.id === kind);
    let uploaded = 0;
    let batchId = null;
    const failures = [];
    // Show a single progress toast we can update in-place.
    const toastId = toast.loading(`Uploading 0 / ${files.length} · ${meta.label}…`);
    try {
      for (const f of files) {
        try {
          const dataUrl = await fileToDataUrl(f);
          const body = { kind, files: [{ data_url: dataUrl, name: f.name }] };
          if (kind === "offline_mom" && batchId) body.batch_id = batchId;
          const { data } = await api.post(`/design/projects/${project.id}/upload-multi`, body);
          uploaded += 1;
          toast.loading(`Uploading ${uploaded} / ${files.length} · ${meta.label}…`, { id: toastId });
          if (kind === "offline_mom" && !batchId) {
            // Capture the just-created batch_id so subsequent files in this
            // call append to the same session.
            const batches = (data?.files || {}).offline_mom || [];
            if (batches.length) batchId = batches[batches.length - 1].batch_id;
          }
        } catch (e) {
          failures.push({ name: f.name, msg: formatApiErrorDetail(e.response?.data?.detail) || "Failed" });
        }
      }
      toast.dismiss(toastId);
      if (uploaded > 0) {
        toast.success(`${uploaded} file${uploaded === 1 ? "" : "s"} uploaded · ${meta.label}`);
      }
      failures.forEach(f => toast.error(`${f.name}: ${f.msg}`));
      onChanged();
    } finally { setBusy(null); }
  };

  const uploadSingle = async (kind, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setBusy(kind);
    try {
      const data_url = await fileToDataUrl(file);
      await api.post(`/design/projects/${project.id}/upload`, {
        kind, data_url, name: file.name,
      });
      const meta = DESIGN_FILE_KINDS.find(k => k.id === kind);
      toast.success(`${meta.label} uploaded`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const removeFile = async (kind, fileId, batchId = null) => {
    if (!window.confirm("Remove this file? This cannot be undone.")) return;
    try {
      await api.post(`/design/projects/${project.id}/remove-file`, {
        kind, file_id: fileId, batch_id: batchId,
      });
      toast.success("File removed");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">Document management</p>
        <p className="text-[10px] font-mono text-gray-600" data-testid="upload-size-hint">
          Up to <span className="text-[#d4af37]">25MB per file</span> · PDF / DWG / DXF / JPG / PNG / WEBP · uploaded one-by-one
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {DESIGN_FILE_KINDS.map(k => (
          <FileKindCard
            key={k.id}
            meta={k}
            project={project}
            canEdit={canEdit}
            busy={busy === k.id}
            onUploadMulti={(files) => uploadMulti(k.id, files)}
            onUploadSingle={(files) => uploadSingle(k.id, files)}
            onRemove={(fileId, batchId) => removeFile(k.id, fileId, batchId)}
          />
        ))}
      </div>
    </div>
  );
}

function FileKindCard({ meta, project, canEdit, busy, onUploadMulti, onUploadSingle, onRemove }) {
  const raw = (project.files || {})[meta.id];
  const isFinal = meta.id === "final_execution_set";

  if (!meta.multi) {
    // Single-file (signoff_sheet)
    const file = raw && !Array.isArray(raw) ? raw : null;
    return (
      <div className={`iz-card p-4`} data-testid={`file-card-${meta.id}`}>
        <FileCardHeader meta={meta} isFinal={false} count={file ? 1 : 0}/>
        {file && (
          <div className="mt-2 pt-2 border-t border-[#1a1a1a] flex items-center gap-2 text-xs">
            <FileText className="w-3.5 h-3.5 text-[#d4af37]"/>
            <a href={file.data_url} target="_blank" rel="noreferrer" className="text-[#d4af37] hover:underline truncate flex-1">
              {file.name || "View"}
            </a>
            <span className="text-[10px] font-mono text-gray-600">{file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString() : ""}</span>
          </div>
        )}
        {canEdit && (
          <label className={`mt-3 iz-btn-secondary inline-flex items-center gap-2 cursor-pointer w-full justify-center ${busy ? "opacity-60" : ""}`} data-testid={`file-upload-${meta.id}`}>
            <Upload className="w-3.5 h-3.5"/>
            {busy ? "Uploading…" : (file ? "Replace" : "Upload")}
            <input type="file" accept={meta.accept} hidden onChange={(e) => { onUploadSingle(e.target.files); e.target.value = ""; }}/>
          </label>
        )}
      </div>
    );
  }

  // Multi-file
  const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
  const totalCount = meta.id === "offline_mom"
    ? list.reduce((n, b) => n + (b.files?.length || 0), 0)
    : list.length;

  return (
    <div className={`iz-card p-4 ${isFinal ? "border-[#d4af37]/40" : ""}`} data-testid={`file-card-${meta.id}`}>
      <FileCardHeader meta={meta} isFinal={isFinal} count={totalCount}/>

      {/* Rendered file list */}
      {meta.id === "offline_mom" ? (
        <BatchList list={list} canEdit={canEdit} onRemove={onRemove}/>
      ) : (
        <FlatFileList list={list} canEdit={canEdit} onRemove={onRemove} testIdPrefix={meta.id}/>
      )}

      {canEdit && (
        <label
          className={`mt-3 iz-btn-secondary inline-flex items-center gap-2 cursor-pointer w-full justify-center ${busy ? "opacity-60" : ""}`}
          data-testid={`file-upload-${meta.id}`}
        >
          <Upload className="w-3.5 h-3.5"/>
          {busy
            ? "Uploading…"
            : (totalCount > 0
                ? (meta.id === "offline_mom" ? "Upload another batch" : "Add more files")
                : "Upload files")}
          <input
            type="file"
            accept={meta.accept}
            multiple
            hidden
            onChange={(e) => { onUploadMulti(e.target.files); e.target.value = ""; }}
            data-testid={`file-input-${meta.id}`}
          />
        </label>
      )}
    </div>
  );
}

function FileCardHeader({ meta, isFinal, count }) {
  return (
    <div className="flex items-start justify-between mb-1">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white flex items-center gap-2">
          {isFinal && <span className="text-[#d4af37]">🔒</span>}
          {meta.label}
          {count > 0 && (
            <span className="text-[10px] font-mono text-[#d4af37] bg-[#d4af37]/10 px-1.5 py-0.5 rounded" data-testid={`file-count-${meta.id}`}>
              {count}
            </span>
          )}
        </p>
        <p className="text-[10px] text-gray-500 font-mono mt-0.5">{meta.note}</p>
      </div>
      {count > 0 && <Check className="w-4 h-4 text-[#d4af37]" strokeWidth={2}/>}
    </div>
  );
}

function FlatFileList({ list, canEdit, onRemove, testIdPrefix }) {
  if (!list.length) return null;
  return (
    <div className="mt-2 pt-2 border-t border-[#1a1a1a] space-y-1.5" data-testid={`file-list-${testIdPrefix}`}>
      {list.map((f, i) => (
        <div key={f.id || i} className="flex items-center gap-2 text-xs" data-testid={`file-item-${testIdPrefix}-${i}`}>
          <FileText className="w-3.5 h-3.5 text-[#d4af37] shrink-0"/>
          <a href={f.data_url} target="_blank" rel="noreferrer" className="text-[#d4af37] hover:underline truncate flex-1">
            {f.name || "file"}
          </a>
          <span className="text-[10px] font-mono text-gray-600 shrink-0">{f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : ""}</span>
          {canEdit && (
            <button
              onClick={() => onRemove(f.id)}
              className="text-gray-500 hover:text-red-400 transition-colors"
              title="Remove"
              data-testid={`file-remove-${testIdPrefix}-${i}`}
            >
              <Trash2 className="w-3.5 h-3.5"/>
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function BatchList({ list, canEdit, onRemove }) {
  if (!list.length) return null;
  return (
    <div className="mt-2 pt-2 border-t border-[#1a1a1a] space-y-3" data-testid="file-list-offline_mom">
      {list.map((batch, bi) => (
        <div key={batch.batch_id || bi} className="pl-2 border-l border-[#1a1a1a]/60" data-testid={`mom-batch-${bi}`}>
          <div className="flex items-center gap-2 mb-1">
            <Layers className="w-3 h-3 text-[#d4af37]/70"/>
            <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">
              Session {bi + 1} · {batch.uploaded_at ? new Date(batch.uploaded_at).toLocaleString() : ""}
              {batch.by_user_name ? ` · by ${batch.by_user_name}` : ""}
            </span>
          </div>
          <div className="space-y-1">
            {(batch.files || []).map((f, fi) => (
              <div key={f.id || fi} className="flex items-center gap-2 text-xs" data-testid={`mom-file-${bi}-${fi}`}>
                <FileText className="w-3.5 h-3.5 text-[#d4af37] shrink-0"/>
                <a href={f.data_url} target="_blank" rel="noreferrer" className="text-[#d4af37] hover:underline truncate flex-1">
                  {f.name || "file"}
                </a>
                {canEdit && (
                  <button
                    onClick={() => onRemove(f.id, batch.batch_id)}
                    className="text-gray-500 hover:text-red-400 transition-colors"
                    title="Remove"
                    data-testid={`mom-file-remove-${bi}-${fi}`}
                  >
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
